import debounce from 'lodash/debounce'
import forEach from 'lodash/forEach'

const filterObject = (object, callback) => {
    const returnable = {}
    forEach(object, (value, key) => {
        if (callback(value)) {
            returnable[key] = value
        }
    })
    return returnable
}

export default {
    data() {
        return {
            filter_watchers: {},
        }
    },
    computed: {
        storageKey() {
            // We use distinct (per-user and per-company) keys, so that filters don't get mixed up between users or
            // companies.
            return `tableFilter${this.filterName}_${this.$store.getters['user/id']}_${this.$store.getters['user/activeCompanyId']}`
        },
    },
    watch: {
        storageKey(newKey, oldKey) {
            if (newKey === oldKey) {
                return
            }

            this.loadFilters()
            this.$nextTick(() => {
                if (this.$refs.table) {
                    this.$refs.table.getPageData(1)
                }
            })
        },
    },
    methods: {
        /**
         * Parses a default filter object
         * @returns {object} All filter properties with their default value
         */
        getDefaultFilter() {
            const filterSettings = {}

            if (typeof this.filterData === 'function') {
                let filterData = filterObject(this.filterData(), (o) => typeof o.store === 'undefined' || o.store)

                forEach(filterData, (value, key) => {
                    filterSettings[key] = this[key]
                })
            }

            return filterSettings
        },

        /**
         * Gets the old localStorage object and updates the updated value from the object
         * @param prop
         * @param value
         */
        setSingleFilterItem(prop, value) {
            // Get old localStorage
            const settings = JSON.parse(localStorage.getItem(this.storageKey))

            if (settings === null || typeof settings[prop] === 'undefined') {
                return
            }

            if (prop.indexOf('date') !== -1 && value !== null) {
                // Sets value to a date object if property name includes 'date'
                value = new Date(value)
            }
            // Sets new value
            settings[prop] = value

            // Actually parse the updated object to the localStorage
            this.setSessionFilter(settings)
        },

        /**
         * Updates localStorage
         * @param newFilter
         */
        setSessionFilter(newFilter) {
            localStorage.setItem(this.storageKey, JSON.stringify(newFilter))
        },

        /**
         * Sets stored values to the input fields
         * @param filterSettings
         */
        setFieldsValueFromStorage(filterSettings) {
            let fieldsAppliedFromStorage = false;

            Object.keys(filterSettings).forEach((key) => {
                if (filterSettings[key] === this[key]) {
                    return
                }

                // If both settings are an object/array, we need to compare them differently
                if (typeof filterSettings[key] === 'object' && typeof this[key] === 'object') {
                    if (JSON.stringify(filterSettings[key]) === JSON.stringify(this[key])) {
                        return
                    }
                }

                if (key.indexOf('date') !== -1 && filterSettings[key] !== null) {
                    filterSettings[key] = new Date(filterSettings[key])
                }

                this[key] = filterSettings[key]

                fieldsAppliedFromStorage = true
            })

            if (
                fieldsAppliedFromStorage === true &&
                typeof this.onSavedFilterDataApplied === 'function'
            ) {
                this.onSavedFilterDataApplied(filterSettings)
            }
        },

        /**
         * Debounced watcher
         * This method will update the sessionData for the current filter property
         * This will also set the tables page to `1` this is needed so the table
         * will update with the new filterdata
         */
        debouncedFilterWatcher: debounce(function (self, key, value) {
            self.setSingleFilterItem(key, value)

            if (this.$refs.table) {
                this.$refs.table.getPageData(1)
            }
        }, 350),
        filterWatcher(key, value) {
            this.setSingleFilterItem(key, value)

            if (this.$refs.table) {
                this.$refs.table.getPageData(1)
            }
        },

        loadFilters() {
            const storedFilter = localStorage.getItem(this.storageKey)
            let parsedFilter = JSON.parse(storedFilter)

            // If no filter is stored yet, or a specific filter key is missing, we append the default values to it
            if (
                parsedFilter === null ||
                Object.keys(this._filterDefaults).every(key => Object.keys(parsedFilter).includes(key)) === false
            ) {
                // Keys have changed since the last stored session. Clear all stored data and start fresh
                parsedFilter = { ...this._filterDefaults }

                this.setSessionFilter(parsedFilter)
            }

            this.setFieldsValueFromStorage(parsedFilter)
        },
    },
    created() {
        /**
         * Read the default starting values from data() before loadFilters() overwrites them.
         * When switching to a company with no saved filters yet, these are used as defaults,
         * preventing another company's values from leaking in.
         */
        const defaultFilter = JSON.stringify(this.getDefaultFilter())
        this._filterDefaults = JSON.parse(defaultFilter)
        this.loadFilters()

        /**
         * Set watchers for every filter property
         */
        if (typeof this.filterData === 'function') {
            const filters = this.filterData()

            for (const key in filters) {
                if (typeof this.filter_watchers[key] !== 'undefined') {
                    return
                }

                if (typeof filters[key].debounced !== 'undefined' && filters[key].debounced) {
                    this.filter_watchers[key] = this.$watch(key, (newValue, oldValue) => {
                        if (newValue === oldValue) {
                            return
                        }

                        this.debouncedFilterWatcher(this, key, newValue)
                    })
                } else {
                    this.filter_watchers[key] = this.$watch(key, (newValue, oldValue) => {
                        if (newValue === oldValue) {
                            return
                        }

                        this.filterWatcher(key, newValue)
                    })
                }
            }
        }
    },
}
